import { Close } from '@mui/icons-material';
import { Avatar, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { Labels } from '@/models/labels';
import { getTimeAgo } from '@/utils/datesAndTime/datesAndTime';

import { TOOLTIP_CARD_SX } from '../constants';
import { buildLabelText, getInitials } from '../utils/text';

type Props = {
  label: Labels.Details;
  isCurrentUser: boolean;
  isPendingConfirmation?: boolean;
  isHighlighted?: boolean;
  onDelete: (e: React.MouseEvent<HTMLElement>) => void;
};

const resolveAssignee = (
  label: Labels.Details,
  isCurrentUser: boolean
): string => (isCurrentUser ? 'You' : label.assignedBy?.name ?? 'Unknown');

const buildTooltipContent = (label: Labels.Details, isCurrentUser: boolean) => (
  <Stack direction="row" gap={1} alignItems="flex-start">
    {!isCurrentUser && label.assignedBy?.name && (
      <Avatar sx={{ width: 24, height: 24 }}>
        <Typography fontWeight={700} fontSize={10} color="#FFF">
          {getInitials(label.assignedBy.name)}
        </Typography>
      </Avatar>
    )}
    <Stack gap={0.25}>
      <Typography variant="body2" fontWeight={700}>
        Added by {resolveAssignee(label, isCurrentUser)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {getTimeAgo(label.assignedAt)}.
      </Typography>
    </Stack>
  </Stack>
);

export const LabelChip = ({
  label,
  isCurrentUser,
  isPendingConfirmation = false,
  isHighlighted = false,
  onDelete
}: Props) => (
  <Tooltip
    title={buildTooltipContent(label, isCurrentUser)}
    slotProps={{ tooltip: { sx: TOOLTIP_CARD_SX } }}
  >
    <Chip
      label={buildLabelText(label)}
      onDelete={onDelete}
      deleteIcon={<Close />}
      variant="outlined"
      sx={{
        borderRadius: 4,
        ...(isPendingConfirmation && {
          borderColor: 'error.main',
          borderWidth: 1.5
        }),
        ...(isHighlighted && {
          borderColor: 'warning.main',
          borderWidth: 1.5,
          bgcolor: 'warning.light'
        }),
        '& .MuiChip-deleteIcon': { fontSize: '16px' }
      }}
    />
  </Tooltip>
);
